import { Badge, Button, FormField, Modal, Select, Textarea } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  productionOrderSourceSchema,
  productionOrderStatusSchema,
  type ProductionOrderListItem,
} from '@warehouse/shared';
import { Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { createOrder, fetchOrders, productionKeys, unwrap } from '../api';
import { ArticlePicker, ProductionAreaPicker } from '../components/Pickers';
import { PRODUCTION_ORDER_TONES } from '../lib/status';
import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { usePermission } from '../../../auth/auth-store';
import { formatDate, formatQty } from '../../../lib/format';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useListState } from '../../../hooks/useListState';
import { useLiveQuery } from '../../../realtime/useLiveQuery';

/**
 * The production order register (spec §5.9). WMS-first: the "New production
 * order" button is the thing the old WMS never had — there, orders could only
 * arrive from Business Central (recon A1).
 */
export function ProductionOrdersPage(): ReactNode {
  const { t } = useTranslation('production');
  const navigate = useNavigate();
  const { state, setState, setFilter } = useListState({ defaultSort: 'createdAt', defaultDir: 'desc' });
  const canManage = usePermission('production.orders.manage');
  const [creating, setCreating] = useState(false);

  const query = useQuery({
    queryKey: productionKeys.orders(state),
    queryFn: () => fetchOrders(state),
  });
  useLiveQuery(['production'], ['production', 'orders']);

  const columns: DataTableColumn<ProductionOrderListItem>[] = [
    {
      id: 'orderNo',
      header: t('orders.columns.orderNo'),
      sortField: 'orderNo',
      cell: (row) => <span className="font-mono text-sm">{row.orderNo}</span>,
    },
    {
      id: 'article',
      header: t('orders.columns.article'),
      sortField: 'articleName',
      cell: (row) => (
        <div className="min-w-0">
          <div className="truncate">{row.outputArticleName}</div>
          <div className="truncate font-mono text-xs text-ink-muted">{row.outputArticleNo}</div>
        </div>
      ),
    },
    {
      id: 'qty',
      header: t('orders.columns.qty'),
      sortField: 'qtyBase',
      align: 'right',
      cell: (row) => formatQty(row.qtyBase, { unit: row.unitCode ?? undefined }),
    },
    {
      id: 'produced',
      header: t('orders.columns.produced'),
      align: 'right',
      cell: (row) => formatQty(row.producedQtyBase),
    },
    {
      id: 'status',
      header: t('orders.columns.status'),
      sortField: 'status',
      cell: (row) => (
        <Badge tone={PRODUCTION_ORDER_TONES[row.status]} dot minWidth>
          {t(`orders.status.${row.status}`)}
        </Badge>
      ),
    },
    {
      id: 'plannedStart',
      header: t('orders.columns.plannedStart'),
      sortField: 'plannedStart',
      hideBelow: 'md',
      cell: (row) => (row.plannedStart ? formatDate(row.plannedStart) : '—'),
    },
    {
      id: 'floorArea',
      header: t('orders.columns.floorArea'),
      hideBelow: 'lg',
      cell: (row) => row.floorAreaName ?? '—',
    },
    {
      id: 'openTasks',
      header: t('orders.columns.openTasks'),
      align: 'right',
      hideBelow: 'lg',
      cell: (row) => (row.openTaskCount > 0 ? row.openTaskCount : '—'),
    },
    {
      id: 'source',
      header: t('orders.columns.source'),
      hideBelow: 'xl',
      cell: (row) => <Badge tone="neutral">{t(`orders.source.${row.source}`)}</Badge>,
    },
  ];

  return (
    <>
      <PageHeader
        title={t('orders.title')}
        subtitle={t('orders.subtitle')}
        actions={
          canManage ? (
            <Button
              icon={<Plus className="size-4" />}
              onClick={() => {
                setCreating(true);
              }}
            >
              {t('orders.new')}
            </Button>
          ) : null
        }
      />

      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={query.data?.total ?? null}
        loading={query.isLoading}
        error={query.error}
        searchPlaceholder={t('orders.searchPlaceholder')}
        emptyTitle={t('orders.empty')}
        emptyDescription={t('orders.emptyDescription')}
        onRowClick={(row) => {
          void navigate(`/production/orders/${row.id}`);
        }}
        filters={
          <>
            <Select
              aria-label={t('orders.filters.status')}
              className="w-44"
              options={[
                { value: '', label: t('orders.filters.allStatuses') },
                ...productionOrderStatusSchema.options.map((status) => ({
                  value: status,
                  label: t(`orders.status.${status}`),
                })),
              ]}
              value={String(state.filters?.status ?? '')}
              onChange={(event) => {
                setFilter('status', event.target.value || undefined);
              }}
            />
            <Select
              aria-label={t('orders.filters.source')}
              className="w-40"
              options={[
                { value: '', label: t('orders.filters.allSources') },
                ...productionOrderSourceSchema.options.map((source) => ({
                  value: source,
                  label: t(`orders.source.${source}`),
                })),
              ]}
              value={String(state.filters?.source ?? '')}
              onChange={(event) => {
                setFilter('source', event.target.value || undefined);
              }}
            />
          </>
        }
      />

      {creating ? (
        <CreateOrderModal
          onClose={() => {
            setCreating(false);
          }}
        />
      ) : null}
    </>
  );
}

function CreateOrderModal({ onClose }: { onClose: () => void }): ReactNode {
  const { t } = useTranslation('production');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [outputArticleId, setOutputArticleId] = useState('');
  const [qtyBase, setQtyBase] = useState('');
  const [orderNo, setOrderNo] = useState('');
  const [plannedStart, setPlannedStart] = useState('');
  const [floorAreaId, setFloorAreaId] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | undefined>();

  const mutation = useMutation({
    mutationFn: () =>
      createOrder({
        outputArticleId,
        qtyBase,
        ...(orderNo.trim() ? { orderNo: orderNo.trim() } : {}),
        ...(plannedStart ? { plannedStart } : {}),
        ...(floorAreaId ? { floorAreaId } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: (result) => {
      toastSuccess(t('orders.toasts.created'));
      void queryClient.invalidateQueries({ queryKey: productionKeys.all });
      onClose();
      void navigate(`/production/orders/${unwrap(result).id}`);
    },
    onError: (err) => {
      toastError(err);
    },
  });

  function submit(): void {
    if (!outputArticleId || !qtyBase.trim()) {
      setError(t('orders.form.outputArticle'));
      return;
    }
    setError(undefined);
    mutation.mutate();
  }

  return (
    <Modal open onClose={onClose} title={t('orders.form.createTitle')} width={560}>
      <div className="flex flex-col gap-4">
        <ArticlePicker
          value={outputArticleId}
          onChange={setOutputArticleId}
          label={t('orders.form.outputArticle')}
          error={error}
        />
        <FormField
          label={t('orders.form.qty')}
          inputMode="decimal"
          value={qtyBase}
          onChange={(event) => {
            setQtyBase(event.target.value);
          }}
        />
        <FormField
          label={t('orders.form.orderNo')}
          hint={<span className="text-xs text-ink-muted">{t('orders.form.orderNoHint')}</span>}
          value={orderNo}
          onChange={(event) => {
            setOrderNo(event.target.value);
          }}
        />
        <FormField
          label={t('orders.form.plannedStart')}
          type="date"
          value={plannedStart}
          onChange={(event) => {
            setPlannedStart(event.target.value);
          }}
        />
        <ProductionAreaPicker
          value={floorAreaId}
          onChange={setFloorAreaId}
          label={t('orders.form.floorArea')}
        />
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-ink">{t('orders.form.note')}</span>
          <Textarea
            rows={2}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
        </label>

        <div className="mt-2 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {t('orders.form.cancel')}
          </Button>
          <Button loading={mutation.isPending} onClick={submit}>
            {t('orders.form.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
